import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { api, qs } from "@/lib/api";
import { Card, SectionTitle } from "@/components/Kpi";
import { AzzeraFiltri, FilterBar, useFiltersMeta } from "@/components/Filters";
import { ComparisonChart } from "@/components/Charts";
import { usePalette } from "@/lib/palette";
import { eurShort, num, troncaNome } from "@/lib/format";

function BloccoGrafico({ g, anno, aperta, onToggle }) {
  const palette = usePalette();
  return (
    <Card className="p-0 overflow-hidden" data-testid={`perf-blocco-${g.id}`}>
      <button type="button" onClick={onToggle} data-testid={`perf-toggle-${g.id}`}
        className={`w-full flex items-center gap-2 px-6 py-3 text-left text-white
          ${g.tipo === "caricato" ? "bg-caricato" : "bg-liquidato"}
          hover:brightness-110 transition-all duration-200`}>
        <ChevronDown size={16} className={`transition-transform duration-200 ${aperta ? "" : "-rotate-90"}`} />
        <span className="uppercase text-xs tracking-[0.18em] font-semibold">{g.titolo}</span>
      </button>
      {aperta && (
        <>
        <div className="p-4 grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2">
            <ComparisonChart testId={`perf-chart-${g.id}`} height={260} labelFormat="perc" coloreDalSegno
              title={`Scostamento % mese su mese · ${anno} vs ${anno - 1}`}
              data={g.righe} keys={[{ key: "scostamento_perc", name: `% vs ${anno - 1}`,
                color: g.tipo === "caricato" ? palette.caricato : palette.liquidato }]} />
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[9px] tracking-wider">
                <tr>
                  <th className="text-left px-2 py-1.5">Mese</th>
                  <th className="text-right px-2 py-1.5">{anno - 1}</th>
                  <th className="text-right px-2 py-1.5">{anno}</th>
                  <th className="text-right px-2 py-1.5">Scost. %</th>
                </tr>
              </thead>
              <tbody>
                {g.righe.map((r, i) => (
                  <tr key={r.mese} className={`border-t border-slate-100 ${i % 2 ? "bg-slate-100/80" : "bg-white"}`}>
                    <td className="px-2 py-1 font-medium whitespace-nowrap">{r.label}</td>
                    <td className="px-2 py-1 text-right font-mono text-slate-500 whitespace-nowrap">{eurShort(r[g.chiave_precedente])}</td>
                    <td className="px-2 py-1 text-right font-mono whitespace-nowrap">{eurShort(r[g.chiave_corrente])}</td>
                    <td className={`px-2 py-1 text-right font-mono font-semibold whitespace-nowrap
                      ${r.scostamento_perc === null ? "text-slate-400"
                        : r.scostamento_perc >= 0 ? "text-emerald-600" : r.scostamento_perc >= -10 ? "text-amber-600" : "text-red-600"}`}>
                      {r.scostamento_perc === null ? "—" : `${num(r.scostamento_perc, 2)}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {g.progressivo?.mese && (
          <div className="px-4 pb-4" data-testid={`perf-progressivo-${g.id}`}>
            <div className="rounded-lg border border-slate-300 bg-slate-100 p-4 flex flex-wrap items-center gap-x-10 gap-y-3">
              <div>
                <div className="uppercase text-[10px] tracking-[0.18em] text-slate-500">
                  Progressivo al {g.progressivo.fino_al_corrente.slice(0, 5)}
                </div>
                <div className="text-sm text-slate-600 mt-1">{g.titolo}</div>
              </div>
              <div>
                <div className="uppercase text-[10px] tracking-[0.18em] text-slate-500">
                  al {g.progressivo.fino_al_precedente}
                </div>
                <div className="font-mono text-lg text-slate-500">{eurShort(g.progressivo.precedente)}</div>
              </div>
              <div>
                <div className="uppercase text-[10px] tracking-[0.18em] text-slate-500">
                  al {g.progressivo.fino_al_corrente}
                </div>
                <div className="font-mono text-lg font-semibold">{eurShort(g.progressivo.corrente)}</div>
              </div>
              <div className="ml-auto text-right">
                <div className="uppercase text-[10px] tracking-[0.18em] text-slate-500">Scostamento</div>
                <div className={`font-mono text-2xl font-semibold
                  ${g.progressivo.scostamento_perc >= 0 ? "text-emerald-600" : g.progressivo.scostamento_perc >= -10 ? "text-amber-600" : "text-red-600"}`}>
                  {g.progressivo.scostamento_perc === null ? "—"
                    : `${g.progressivo.scostamento_perc > 0 ? "+" : ""}${num(g.progressivo.scostamento_perc, 2)}%`}
                </div>
              </div>
            </div>
          </div>
        )}
        </>
      )}
    </Card>
  );
}

/** Performance: scostamento % mese su mese vs anno precedente (caricato e liquidato, totale/AUTO/NON AUTO). */
export default function Performance() {
  const { slug } = useParams();
  const meta = useFiltersMeta();
  const [filters, setFilters] = useState({ anno: 2026, ...(slug ? { commerciale: slug } : {}) });
  const [d, setD] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [chiuse, setChiuse] = useState({});
  const [reset, setReset] = useState(0);

  useEffect(() => {
    api.get(`/dashboard/dealers-periodo?${qs({ anno: filters.anno, commerciale: filters.commerciale })}`)
      .then((r) => setDealers(r.data.dealers || [])).catch(() => setDealers([]));
  }, [filters.anno, filters.commerciale]);

  useEffect(() => {
    setFilters((f) => ({ ...f, commerciale: slug || "" }));
  }, [slug]);

  useEffect(() => {
    api.get(`/dashboard/performance?${qs(filters)}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [filters]);

  const anno = Number(filters.anno) || 2026;

  return (
    <div data-testid="performance-scostamenti-page">
      <SectionTitle sub={`Scostamento % mese su mese ${anno} vs ${anno - 1} · caricato e liquidato, totale e per categoria${
        filters.dealer ? ` · ${filters.dealer.split(",").filter(Boolean).length} convenzionati selezionati` : ""}`}
        right={
          <a href={`/api/report/performance.pdf?${qs(filters)}`} data-testid="btn-report-performance-pdf"
            className="px-4 py-2 rounded-md bg-[#0018A8] text-white text-sm hover:bg-[#001480] transition-colors duration-200">
            Scarica report PDF
          </a>
        }>
        Scostamento vs 2025
      </SectionTitle>

      <div className="flex flex-wrap items-center gap-3">
        <FilterBar key={reset} meta={meta} value={filters} onChange={setFilters} dealers={dealers}
          fields={slug ? ["anno", "settore", "dealer"] : ["anno", "commerciale", "settore", "dealer"]} />
        <AzzeraFiltri onClick={() => {
          setFilters({ anno: 2026, ...(slug ? { commerciale: slug } : { commerciale: "" }) });
          setReset((v) => v + 1);
        }} />
      </div>

      {!d ? (
        <div className="text-sm text-slate-500" data-testid="performance-loading">Caricamento…</div>
      ) : (
        <>
        <div className="space-y-8">
          {["caricato", "liquidato"].map((tipo) => {
            const gruppo = d.grafici.filter((g) => g.tipo === tipo);
            const chiusa = chiuse[`sezione-${tipo}`];
            return (
              <div key={tipo} data-testid={`perf-sezione-${tipo}`}>
                <button type="button" data-testid={`perf-sezione-toggle-${tipo}`}
                  onClick={() => setChiuse((v) => ({ ...v, [`sezione-${tipo}`]: !v[`sezione-${tipo}`] }))}
                  className="w-full flex items-center gap-2 mb-4 px-4 py-2.5 rounded-md border border-slate-200
                    bg-white text-left uppercase text-xs tracking-[0.18em] font-semibold text-slate-600
                    hover:border-slate-400 hover:text-slate-900 transition-colors duration-200">
                  <ChevronDown size={16} className={`transition-transform duration-200 ${chiusa ? "-rotate-90" : ""}`} />
                  Tutto il {tipo}
                  <span className="ml-auto normal-case tracking-normal text-[11px] text-slate-400">
                    {chiusa ? "mostra" : "nascondi"}
                  </span>
                </button>
                {!chiusa && (
                  <div className="space-y-6">
                    {gruppo.map((g) => (
                      <BloccoGrafico key={g.id} g={g} anno={d.anno} aperta={!chiuse[g.id]}
                        onToggle={() => setChiuse((v) => ({ ...v, [g.id]: !v[g.id] }))} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {d.dettaglio_dealer?.length > 0 && (
          <Card className="p-0 overflow-hidden mt-8" data-testid="perf-dettaglio-dealer">
            <div className="px-6 py-3 bg-slate-800 text-white uppercase text-xs tracking-[0.18em] font-semibold">
              Dettaglio convenzionati selezionati · progressivo al {d.grafici?.[0]?.progressivo?.fino_al_corrente?.slice(0, 5) || ""}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">Convenzionato</th>
                    <th className="text-right px-3 py-2">Caricato {d.anno - 1}</th>
                    <th className="text-right px-3 py-2">Caricato {d.anno}</th>
                    <th className="text-right px-3 py-2">Scost. caricato</th>
                    <th className="text-right px-3 py-2">Liquidato {d.anno - 1}</th>
                    <th className="text-right px-3 py-2">Liquidato {d.anno}</th>
                    <th className="text-right px-3 py-2">Scost. liquidato</th>
                  </tr>
                </thead>
                <tbody>
                  {d.dettaglio_dealer.map((r, i) => (
                    <tr key={r.codice_dealer} data-testid={`perf-dettaglio-riga-${r.codice_dealer}`}
                      className={`border-t border-slate-100 ${i % 2 ? "bg-slate-100/80" : "bg-white"}`}>
                      <td className="px-4 py-2 font-bold whitespace-nowrap" title={r.ragione_sociale}>
                        {troncaNome(r.ragione_sociale)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500 whitespace-nowrap">{eurShort(r.caricato_prec)}</td>
                      <td className="px-3 py-2 text-right font-mono whitespace-nowrap">{eurShort(r.caricato_cur)}</td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold whitespace-nowrap
                        ${r.scostamento_caricato === null ? "text-slate-400"
                          : r.scostamento_caricato >= 0 ? "text-emerald-600" : r.scostamento_caricato >= -10 ? "text-amber-600" : "text-red-600"}`}>
                        {r.scostamento_caricato === null ? "—" : `${num(r.scostamento_caricato, 2)}%`}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500 whitespace-nowrap">{eurShort(r.liquidato_prec)}</td>
                      <td className="px-3 py-2 text-right font-mono whitespace-nowrap">{eurShort(r.liquidato_cur)}</td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold whitespace-nowrap
                        ${r.scostamento_liquidato === null ? "text-slate-400"
                          : r.scostamento_liquidato >= 0 ? "text-emerald-600" : r.scostamento_liquidato >= -10 ? "text-amber-600" : "text-red-600"}`}>
                        {r.scostamento_liquidato === null ? "—" : `${num(r.scostamento_liquidato, 2)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 text-[11px] text-slate-500">
              Valori progressivi dal 1° gennaio all'ultimo mese completato di ciascun anno.
            </div>
          </Card>
        )}
        </>
      )}
    </div>
  );
}
